import React from "react";
import Sidebar from "@/components/Sidebar";
import Header from "@/components/Header";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Users, Truck, ShoppingCart, Building2, Package } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { endpoints } from "@/config/endpoints";
import apiService from "@/services/api.service";
import { format } from "date-fns";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

interface Request {
  _id: string;
  status: "pending" | "approved" | "rejected" | "delivered";
  supplierId?: string;
  siteId: string;
  product?: {
    id: string;
  };
}

interface User {
  _id: string;
  name: string;
  email: string;
}

interface Supplier {
  _id: string;
  name: string;
}

interface Site {
  _id: string;
  name: string;
}

interface Product {
  _id: string;
  title: string;
}

interface AnalyticsData {
  totalRequests: number;
  totalUsers: number;
  totalSuppliers: number;
  totalProducts: number;
  totalSites: number;
  percentageChanges: {
    requests: string;
    users: string;
    suppliers: string;
    products: string;
    sites: string;
  };
  requestStatus: {
    pending: number;
    approved: number;
    rejected: number;
    delivered: number;
  };
  userActivity: {
    date: string;
    count: number;
  }[];
  supplierPerformance: {
    name: string;
    requests: number;
  }[];
  siteUtilization: {
    name: string;
    requests: number;
  }[];
  productPerformance: {
    name: string;
    requests: number;
  }[];
}

const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042"];

const Analytics = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = React.useState(false);

  const { data: analyticsData, isLoading } = useQuery<AnalyticsData>({
    queryKey: ["analytics"],
    queryFn: async () => {
      const [requestsRes, usersRes, suppliersRes, productsRes, sitesRes] =
        await Promise.all([
          apiService.get<Request[]>(endpoints.requests.list),
          apiService.get<User[]>(endpoints.auth.users),
          apiService.get<Supplier[]>(endpoints.suppliers.list),
          apiService.get<Product[]>(endpoints.products.list),
          apiService.get<Site[]>(endpoints.sites.list),
        ]);

      // Process request status
      const requestStatus = {
        pending: requestsRes.filter((r) => r.status === "pending").length,
        approved: requestsRes.filter((r) => r.status === "approved").length,
        rejected: requestsRes.filter((r) => r.status === "rejected").length,
        delivered: requestsRes.filter((r) => r.status === "delivered").length,
      };

      // Process user activity (last 7 days)
      const userActivity = Array.from({ length: 7 }, (_, i) => {
        const date = new Date();
        date.setDate(date.getDate() - i);
        return {
          date: format(date, "MMM dd"),
          count: Math.floor(Math.random() * 50) + 10, // Replace with actual data
        };
      }).reverse();

      // Process supplier performance
      const supplierPerformance = suppliersRes.map((supplier) => ({
        name: supplier.name,
        requests: requestsRes.filter((r) => r.supplierId === supplier._id)
          .length,
      }));

      // Process site utilization
      const siteUtilization = sitesRes.map((site) => ({
        name: site.name,
        requests: requestsRes.filter((r) => r.siteId === site._id).length,
      }));

      // Process product performance
      const productPerformance = productsRes.map((product) => ({
        name: product.title,
        requests: requestsRes.filter((r) => r.product?.id === product._id)
          .length,
      }));

      return {
        totalRequests: requestsRes.length,
        totalUsers: usersRes.length,
        totalSuppliers: suppliersRes.length,
        totalProducts: productsRes.length,
        totalSites: sitesRes.length,
        percentageChanges: {
          requests: "+4.2%",
          users: "+1.2%",
          suppliers: "+5.2%",
          products: "+3.2%",
          sites: "+2.2%",
        },
        requestStatus,
        userActivity,
        supplierPerformance,
        siteUtilization,
        productPerformance,
      };
    },
  });

  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  if (isLoading) {
    return (
      <div className="flex h-screen bg-gray-50">
        <Sidebar collapsed={sidebarCollapsed} onToggle={toggleSidebar} />
        <div className="flex-1 flex flex-col overflow-hidden">
          <Header onToggleSidebar={toggleSidebar} />
          <main className="flex-1 overflow-x-hidden overflow-y-auto p-4">
            <div className="flex items-center justify-center h-full">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  const requestStatusData = [
    { name: "Pending", value: analyticsData?.requestStatus.pending },
    { name: "Approved", value: analyticsData?.requestStatus.approved },
    { name: "Rejected", value: analyticsData?.requestStatus.rejected },
    { name: "Delivered", value: analyticsData?.requestStatus.delivered },
  ];

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar collapsed={sidebarCollapsed} onToggle={toggleSidebar} />

      <div className="flex-1 flex flex-col overflow-hidden">
        <Header onToggleSidebar={toggleSidebar} />

        <main className="flex-1 overflow-x-hidden overflow-y-auto p-4">
          <h1 className="text-2xl font-bold mb-6">Analytics Dashboard</h1>

          {/* Overview Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
            <Card className="bg-gradient-to-br from-blue-50 to-blue-100">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg font-medium">
                    Total Users
                  </CardTitle>
                  <Users className="h-5 w-5 text-blue-500" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">
                  {analyticsData?.totalUsers}
                </div>
                <p className="text-sm text-gray-500">
                  {analyticsData?.percentageChanges.users} from last month
                </p>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-green-50 to-green-100">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg font-medium">
                    Suppliers
                  </CardTitle>
                  <Truck className="h-5 w-5 text-green-500" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">
                  {analyticsData?.totalSuppliers}
                </div>
                <p className="text-sm text-gray-500">
                  {analyticsData?.percentageChanges.suppliers} from last month
                </p>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-purple-50 to-purple-100">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg font-medium">
                    Active Requests
                  </CardTitle>
                  <ShoppingCart className="h-5 w-5 text-purple-500" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">
                  {analyticsData?.totalRequests}
                </div>
                <p className="text-sm text-gray-500">
                  {analyticsData?.percentageChanges.requests} from last month
                </p>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-orange-50 to-orange-100">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg font-medium">Sites</CardTitle>
                  <Building2 className="h-5 w-5 text-orange-500" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">
                  {analyticsData?.totalSites}
                </div>
                <p className="text-sm text-gray-500">
                  {analyticsData?.percentageChanges.sites} from last month
                </p>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-red-50 to-red-100">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg font-medium">
                    Products
                  </CardTitle>
                  <Package className="h-5 w-5 text-red-500" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">
                  {analyticsData?.totalProducts}
                </div>
                <p className="text-sm text-gray-500">
                  {analyticsData?.percentageChanges.products} from last month
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Request Status Distribution */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg font-medium">
                  Request Status Distribution
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={requestStatusData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                        label={({ name, percent }) =>
                          `${name} ${(percent * 100).toFixed(0)}%`
                        }
                      >
                        {requestStatusData.map((entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={COLORS[index % COLORS.length]}
                          />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg font-medium">
                  User Activity (Last 7 Days)
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={analyticsData?.userActivity}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="count" fill="#8884d8" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Supplier and Site Performance */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg font-medium">
                  Top Suppliers by Requests
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={analyticsData?.supplierPerformance
                        .sort((a, b) => b.requests - a.requests)
                        .slice(0, 5)}
                      layout="vertical"
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" />
                      <YAxis dataKey="name" type="category" width={150} />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="requests" fill="#82ca9d" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg font-medium">
                  Top Sites by Activity
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={analyticsData?.siteUtilization
                        .sort((a, b) => b.requests - a.requests)
                        .slice(0, 5)}
                      layout="vertical"
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" />
                      <YAxis dataKey="name" type="category" width={150} />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="requests" fill="#ffc658" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Product Performance */}
          <Card className="mb-6">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-medium">
                Top Products by Requests
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={analyticsData?.productPerformance
                      .sort((a, b) => b.requests - a.requests)
                      .slice(0, 10)}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="requests" stroke="#ff7300" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </main>

        <footer className="bg-white p-4 text-center text-gray-500 text-sm border-t">
          Copyright © 2025. All right reserved.
        </footer>
      </div>
    </div>
  );
};

export default Analytics;
